/**
 * Stale-Chunk-Schutz nach einem Deploy.
 *
 * Die App ist code-gesplittet: jede Route ist ein eigener, hash-benannter
 * JS-Chunk (z. B. `VergleichPage-BitkjxBB.js`). Bei einem Deploy bekommen
 * diese Chunks NEUE Hashes und die alten Dateien verschwinden vom Server
 * (Coolify baut den Container neu). Ein Tab, der die App VOR dem Deploy
 * geladen hat, kennt aber noch die ALTEN Dateinamen. Navigiert der Nutzer
 * dann auf eine noch nicht geladene Route, fordert der Browser den alten
 * (jetzt gelöschten) Chunk an → "Failed to fetch dynamically imported module".
 *
 * Das wirkt „random", weil es nur Nutzer trifft, die einen Tab über einen
 * Deploy hinweg offen hatten — und nur beim Lazy-Navigieren. Bei häufigen
 * Deploys passiert es entsprechend öfter.
 *
 * Lösung: bei genau diesem Fehler EINMAL hart neu laden (holt frisches
 * index.html + aktuelle Chunk-Namen). Ein sessionStorage-Zeitstempel
 * verhindert Endlos-Reload-Loops, falls der Fehler doch eine andere Ursache
 * hat (dann greift die normale Fehlerseite).
 */

const CHUNK_RELOAD_KEY = 'immokompass:chunk-reload-at';
const RELOAD_DEBOUNCE_MS = 15000;

/** Erkennt die typischen „dynamischer Import fehlgeschlagen"-Meldungen (Browser-übergreifend). */
export function isChunkLoadError(err: unknown): boolean {
  const msg =
    err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? '');
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|dynamically imported module|ChunkLoadError|Loading chunk \d+ failed/i.test(
    msg
  );
}

/**
 * Löst genau einen Hard-Reload aus (debounced via sessionStorage).
 * @returns true, wenn ein Reload angestoßen wurde; false, wenn kürzlich
 *          schon einer lief (→ Aufrufer sollte regulär den Fehler zeigen).
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0');
    if (Number.isFinite(last) && Date.now() - last < RELOAD_DEBOUNCE_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}
